import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch } from "../../utils/api";
import '../../styles/admin/admin-form.css'; 

const DIAS_SEMANA = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

// Inicialización segura del horario
const HORARIO_POR_DEFECTO = DIAS_SEMANA.reduce((acc, dia) => {
  acc[dia] = [];
  return acc;
}, {});

// Estado inicial para un objeto de almacenamiento nuevo
const ALMACEN_VACIO = {
  tipo: '',
  capacidad: '',
  area: '',
  caracteristicas: '',
  observaciones: '',
  imagen: ''
};

export default function FormularioPuerto() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id); 

  const [formData, setFormData] = useState({
    nombre: '',
    correoElectronico: '',
    telefono: '',
    municipio: '',
    estado: '',
    direccion: '',
    descripcion: '', 
    imagenUrl: '',
    urlImagenInferior: '', 
    urlWeb: '', 
    ruta: [], 
    servicios: [], 
    almacenamiento: [], 
    horario: HORARIO_POR_DEFECTO 
  });

  const [nuevaRuta, setNuevaRuta] = useState(''); 
  const [nuevoServicio, setNuevoServicio] = useState(''); 
  
  const [nuevoAlmacen, setNuevoAlmacen] = useState(ALMACEN_VACIO);

  const [generalHorario, setGeneralHorario] = useState([{ abre: "", cierra: "" }]);

  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditMode) {
      setPageLoading(true);
      setError(null);
      
      authFetch(`/api/Puerto/${id}`)
        .then(res => {
          if (!res.ok) throw new Error('No se pudieron cargar los datos del puerto.');
          return res.json();
        })
        .then(data => {
          setFormData({
            nombre: data.nombre || '',
            correoElectronico: data.correoElectronico || '',
            telefono: data.telefono || '',
            municipio: data.municipio || '',
            estado: data.estado || '',
            direccion: data.direccion || '',
            descripcion: data.descripcion || data.Descripcion || '', 
            imagenUrl: data.urlImagen || data.UrlImagen || data.url_imagen || '', 
            urlImagenInferior: data.urlImagenInferior || data.UrlImagenInferior || data.url_imagen_inferior || '', // Mapeo
            urlWeb: data.urlWeb || data.UrlWeb || data.url_web || '', // Mapeo
            ruta: data.ruta || data.Ruta || [], 
            servicios: data.servicios || data.Servicios || [], 
            almacenamiento: data.almacenamiento || data.Almacenamiento || [], // Mapeo
            horario: { ...HORARIO_POR_DEFECTO, ...(data.horario || {}) }
          });
        })
        .catch(err => {
          console.error(err);
          setError(err.message);
        })
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, [id, isEditMode]); 

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- LÓGICA RUTAS ---
  const handleAddRuta = () => {
    if (!nuevaRuta.trim()) return; 
    const items = nuevaRuta.split(/[\n,]+/).map(item => item.trim()).filter(item => item !== '');
    setFormData(prev => ({ ...prev, ruta: [...(prev.ruta || []), ...items] }));
    setNuevaRuta(''); 
  };

  const handleRemoveRuta = (index) => {
    setFormData(prev => ({ ...prev, ruta: (prev.ruta || []).filter((_, i) => i !== index) }));
  };

  // --- LÓGICA SERVICIOS ---
  const handleAddServicio = () => {
    if (!nuevoServicio.trim()) return; 
    const items = nuevoServicio.split(/[\n,]+/).map(item => item.trim()).filter(item => item !== '');
    setFormData(prev => ({ ...prev, servicios: [...(prev.servicios || []), ...items] }));
    setNuevoServicio(''); 
  };

  const handleRemoveServicio = (index) => {
    setFormData(prev => ({ ...prev, servicios: (prev.servicios || []).filter((_, i) => i !== index) }));
  };

  // --- LÓGICA ALMACENAMIENTO (NUEVO) ---
  const handleAlmacenInput = (e) => {
    const { name, value } = e.target;
    setNuevoAlmacen(prev => ({ ...prev, [name]: value }));
  };

  const handleAddAlmacen = () => {
    // Validación mínima
    if (!nuevoAlmacen.tipo.trim()) {
      alert("Debes indicar al menos el Tipo de almacenamiento (Ej. Patio, Bodega).");
      return;
    }
    setFormData(prev => ({
      ...prev,
      almacenamiento: [...(prev.almacenamiento || []), nuevoAlmacen]
    }));
    setNuevoAlmacen(ALMACEN_VACIO); // Reset inputs
  };

  const handleRemoveAlmacen = (index) => {
    setFormData(prev => ({
      ...prev,
      almacenamiento: (prev.almacenamiento || []).filter((_, i) => i !== index)
    }));
  };

  // --- LÓGICA HORARIOS ---
  const handleHorarioChange = (dia, index, campo, value) => {
    setFormData(prev => {
      const nuevoHorario = JSON.parse(JSON.stringify(prev.horario));
      if (nuevoHorario[dia] && nuevoHorario[dia][index]) {
        nuevoHorario[dia][index][campo] = value;
      }
      return { ...prev, horario: nuevoHorario };
    });
  };

  const addTurno = (dia) => {
    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      const currentDay = Array.isArray(nuevoHorario[dia]) ? nuevoHorario[dia] : [];
      nuevoHorario[dia] = [...currentDay, { abre: '', cierra: '' }];
      return { ...prev, horario: nuevoHorario };
    });
  };

  const removeTurno = (dia, index) => {
    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      if (Array.isArray(nuevoHorario[dia])) {
        nuevoHorario[dia] = nuevoHorario[dia].filter((_, i) => i !== index);
      }
      return { ...prev, horario: nuevoHorario };
    });
  };

  // --- CONFIGURACIÓN MASIVA HORARIOS ---
  const handleGeneralChange = (index, campo, value) => {
    const nuevos = [...generalHorario];
    nuevos[index] = { ...nuevos[index], [campo]: value };
    setGeneralHorario(nuevos);
  };

  const addGeneralTurn = () => setGeneralHorario([...generalHorario, { abre: "", cierra: "" }]);
  const removeGeneralTurn = (index) => setGeneralHorario(generalHorario.filter((_, i) => i !== index));

  const aplicarHorarioGeneral = () => {
    const turnosValidos = generalHorario.filter(t => t.abre && t.cierra);
    if (turnosValidos.length === 0) {
      alert("⚠️ Define al menos un turno completo.");
      return;
    }
    if (!window.confirm(`¿Aplicar estos turnos a LUNES-VIERNES?`)) return;

    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      ["lunes", "martes", "miercoles", "jueves", "viernes"].forEach(dia => {
        nuevoHorario[dia] = turnosValidos.map(t => ({ ...t }));
      });
      return { ...prev, horario: nuevoHorario };
    });
    alert("✅ Horario aplicado.");
  };

  // --- SUBMIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const url = isEditMode ? `/api/Puerto/${id}` : `/api/Puerto`;
    const method = isEditMode ? 'PUT' : 'POST';

    const payload = {
      ...(isEditMode && { id: id }), 
      nombre: formData.nombre,
      correoElectronico: formData.correoElectronico,
      telefono: formData.telefono,
      municipio: formData.municipio,
      estado: formData.estado,
      direccion: formData.direccion,
      horario: formData.horario,
      Descripcion: formData.descripcion, 
      UrlImagen: formData.imagenUrl,
      UrlImagenInferior: formData.urlImagenInferior,
      UrlWeb: formData.urlWeb,
      Almacenamiento: formData.almacenamiento,
      Ruta: formData.ruta,
      Servicios: formData.servicios
    };
    
    try {
      const response = await authFetch(url, {
        method: method,
        body: JSON.stringify(payload), 
      });

      if (!response.ok) {
        const errorText = await response.text(); 
        throw new Error(errorText || 'Error desconocido al guardar.');
      }

      alert(isEditMode ? 'Puerto actualizado exitosamente' : 'Puerto guardado exitosamente');
      navigate('/admin/puertos'); 
    } catch (err) {
      console.error(err);
      setError(err.message);
      alert('Error al guardar: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container">
      <button className="btn-back" onClick={() => navigate(-1)} disabled={isSubmitting}>
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>
        Volver
      </button>

      <h1>{isEditMode ? 'Editar Puerto' : 'Añadir Nuevo Puerto'}</h1>

      {error && <div className="form-error">Error: {error}</div>}
      {pageLoading && <div className="form-loading">Cargando datos del puerto...</div>}

      {!pageLoading && (
        <form onSubmit={handleSubmit}>
          
          {/* --- DATOS GENERALES --- */}
          <div className="form-group">
            <label htmlFor="nombre">Nombre del Puerto</label>
            <input id="nombre" name="nombre" type="text" placeholder="Ej. Puerto de Veracruz" value={formData.nombre} onChange={handleChange} disabled={isSubmitting} required />
          </div>

          <div className="form-row">
             <div className="form-group">
                <label htmlFor="imagenUrl">URL Imagen Principal</label>
                <input id="imagenUrl" name="imagenUrl" type="url" placeholder="Ej. https://..." value={formData.imagenUrl} onChange={handleChange} disabled={isSubmitting} />
             </div>
             <div className="form-group">
                <label htmlFor="urlImagenInferior">URL Imagen Inferior</label>
                <input id="urlImagenInferior" name="urlImagenInferior" type="url" placeholder="Ej. https://..." value={formData.urlImagenInferior} onChange={handleChange} disabled={isSubmitting} />
             </div>
          </div>

          <div className="form-group">
             <label htmlFor="urlWeb">Sitio Web Oficial</label>
             <input id="urlWeb" name="urlWeb" type="url" placeholder="Ej. https://www.puertoveracruz.com.mx" value={formData.urlWeb} onChange={handleChange} disabled={isSubmitting} />
          </div>

          <div className="form-group">
            <label htmlFor="correoElectronico">Correo Electrónico</label>
            <input id="correoElectronico" name="correoElectronico" type="email" placeholder="Ej. contacto@puertoveracruz.com" value={formData.correoElectronico} onChange={handleChange} disabled={isSubmitting} />
          </div>

          <div className="form-group">
            <label htmlFor="telefono">Teléfono</label>
            <input id="telefono" name="telefono" type="tel" placeholder="Ej. 229 123 4567" value={formData.telefono} onChange={handleChange} disabled={isSubmitting} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="municipio">Municipio</label>
              <input id="municipio" name="municipio" type="text" placeholder="Ej. Veracruz" value={formData.municipio} onChange={handleChange} disabled={isSubmitting} />
            </div>
            <div className="form-group">
              <label htmlFor="estado">Estado</label>
              <input id="estado" name="estado" type="text" placeholder="Ej. Veracruz" value={formData.estado} onChange={handleChange} disabled={isSubmitting} />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="direccion">Dirección Completa</label>
            <textarea id="direccion" name="direccion" placeholder="Ej. Av. Marina Mercante 210..." rows="2" value={formData.direccion} onChange={handleChange} disabled={isSubmitting}></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="descripcion">Descripción</label>
            <textarea id="descripcion" name="descripcion" placeholder="Ej. Puerto principal para carga comercial..." rows="3" value={formData.descripcion} onChange={handleChange} disabled={isSubmitting}></textarea>
          </div>

          {/* --- SECCIÓN ALMACENAMIENTO  --- */}
          <div className="form-group" style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '8px', background: '#fcfcfc' }}>
            <label style={{ fontSize: '1.1rem', color: '#2c3e50' }}>🏭 Infraestructura de Almacenamiento</label>
            <p style={{fontSize: '0.85em', color: '#666', marginBottom: '15px'}}>Agrega bodegas, patios o terminales disponibles.</p>
            
            {/* Formulario para agregar item */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                <input name="tipo" placeholder="Tipo (Ej. Bodega)" value={nuevoAlmacen.tipo} onChange={handleAlmacenInput} style={{padding: '8px', border: '1px solid #ccc', borderRadius:'4px'}} />
                <input name="capacidad" placeholder="Capacidad (Ej. 45,000 Ton)" value={nuevoAlmacen.capacidad} onChange={handleAlmacenInput} style={{padding: '8px', border: '1px solid #ccc', borderRadius:'4px'}} />
                <input name="area" placeholder="Área (Ej. 12,000 m2)" value={nuevoAlmacen.area} onChange={handleAlmacenInput} style={{padding: '8px', border: '1px solid #ccc', borderRadius:'4px'}} />
                <input name="caracteristicas" placeholder="Características (Techada, etc)" value={nuevoAlmacen.caracteristicas} onChange={handleAlmacenInput} style={{padding: '8px', border: '1px solid #ccc', borderRadius:'4px'}} />
                <input name="imagen" placeholder="URL Imagen (Opcional)" value={nuevoAlmacen.imagen} onChange={handleAlmacenInput} style={{padding: '8px', border: '1px solid #ccc', borderRadius:'4px'}} />
            </div>
            <textarea name="observaciones" placeholder="Observaciones adicionales..." value={nuevoAlmacen.observaciones} onChange={handleAlmacenInput} rows="2" style={{width: '100%', marginBottom: '10px', padding: '8px', border: '1px solid #ccc', borderRadius:'4px'}} />
            
            <button type="button" onClick={handleAddAlmacen} className="btn-add-turno" style={{ width: '100%', marginBottom: '20px' }}>+ Agregar Infraestructura</button>

            {/* Lista de items agregados */}
            <div className="almacen-list">
                {(formData.almacenamiento || []).map((item, index) => (
                    <div key={index} style={{ background: 'white', border: '1px solid #eee', padding: '10px', borderRadius: '6px', marginBottom: '8px', position: 'relative', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <button type="button" onClick={() => handleRemoveAlmacen(index)} style={{ position: 'absolute', top: '5px', right: '5px', color: 'red', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem' }}>&times;</button>
                        <strong>{item.tipo}</strong>
                        <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                            <span>📦 Cap: {item.capacidad}</span>
                            <span>📐 Área: {item.area}</span>
                        </div>
                        {item.caracteristicas && <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '4px' }}>📝 {item.caracteristicas}</div>}
                    </div>
                ))}
                {(!formData.almacenamiento || formData.almacenamiento.length === 0) && <p style={{ textAlign: 'center', color: '#aaa', fontStyle: 'italic' }}>No hay infraestructura registrada.</p>}
            </div>
          </div>

          {/* --- SECCIÓN RUTAS --- */}
          <div className="form-group">
            <label>Rutas Logísticas</label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                <textarea placeholder="Ej. Ruta A&#10;Ruta B" value={nuevaRuta} onChange={(e) => setNuevaRuta(e.target.value)} rows="3" style={{ flex: 1 }} />
                <button type="button" onClick={handleAddRuta} className="btn-add-turno" style={{ marginTop: '5px' }}>+ Lista</button>
            </div>
            <ul style={{ listStyle: 'none', padding: 0 }}>
                {(formData.ruta || []).map((ruta, index) => (
                    <li key={index} style={{ background: '#f8f9fa', padding: '8px', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', border: '1px solid #e9ecef' }}>
                        <span>• {ruta}</span>
                        <button type="button" onClick={() => handleRemoveRuta(index)} style={{ color: 'red', border: 'none', background: 'transparent', cursor: 'pointer' }}>&times;</button>
                    </li>
                ))}
            </ul>
          </div>

          {/* --- SECCIÓN SERVICIOS --- */}
          <div className="form-group">
            <label>Servicios Ofrecidos</label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                <textarea placeholder="Ej. Carga General&#10;Fluidos" value={nuevoServicio} onChange={(e) => setNuevoServicio(e.target.value)} rows="3" style={{ flex: 1 }} />
                <button type="button" onClick={handleAddServicio} className="btn-add-turno" style={{ marginTop: '5px' }}>+ Lista</button>
            </div>
            <ul style={{ listStyle: 'none', padding: 0 }}>
                {(formData.servicios || []).map((servicio, index) => (
                    <li key={index} style={{ background: '#f8f9fa', padding: '8px', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', border: '1px solid #e9ecef' }}>
                        <span>• {servicio}</span>
                        <button type="button" onClick={() => handleRemoveServicio(index)} style={{ color: 'red', border: 'none', background: 'transparent', cursor: 'pointer' }}>&times;</button>
                    </li>
                ))}
            </ul>
          </div>

          {/* --- HORARIOS --- */}
          <fieldset>
            <legend>Horarios de Atención</legend>
            <div style={{ backgroundColor: '#e0f2fe', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '2px solid #0ea5e9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: '#0369a1' }}>⚡ Configurar Horario General (L-V)</h4>
                <button type="button" onClick={addGeneralTurn} style={{ fontSize: '0.8em', padding: '4px 8px', cursor: 'pointer' }}>+ Turno</button>
              </div>
              {generalHorario.map((turno, index) => (
                <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '5px' }}>
                  <input type="time" value={turno.abre} onChange={(e) => handleGeneralChange(index, 'abre', e.target.value)} style={{ padding: '6px' }} />
                  <input type="time" value={turno.cierra} onChange={(e) => handleGeneralChange(index, 'cierra', e.target.value)} style={{ padding: '6px' }} />
                  {generalHorario.length > 1 && <button type="button" onClick={() => removeGeneralTurn(index)} style={{ color: 'red', border: 'none', cursor: 'pointer' }}>&times;</button>}
                </div>
              ))}
              <button type="button" onClick={aplicarHorarioGeneral} style={{ marginTop: '10px', backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '8px', borderRadius: '4px', cursor: 'pointer' }}>Aplicar</button>
            </div>

            {DIAS_SEMANA.map((dia) => (
              <div key={dia} className="horario-dia">
                <div className="horario-dia-header">
                  <h3>{dia.charAt(0).toUpperCase() + dia.slice(1)}</h3>
                  <button type="button" onClick={() => addTurno(dia)} className="btn-add-turno" disabled={isSubmitting}>+ Añadir</button>
                </div>
                {(!formData.horario[dia] || formData.horario[dia].length === 0) && <p className="horario-cerrado">Cerrado</p>}
                {(formData.horario[dia] || []).map((turno, index) => (
                  <div key={index} className="horario-turno">
                    <div className="horario-inputs">
                      <input type="time" value={turno.abre} onChange={(e) => handleHorarioChange(dia, index, 'abre', e.target.value)} disabled={isSubmitting} />
                      <input type="time" value={turno.cierra} onChange={(e) => handleHorarioChange(dia, index, 'cierra', e.target.value)} disabled={isSubmitting} />
                    </div>
                    <button type="button" onClick={() => removeTurno(dia, index)} className="btn-remove-turno" disabled={isSubmitting}>&times;</button>
                  </div>
                ))}
              </div>
            ))}
          </fieldset>
          
          <button type="submit" className="btn-submit" disabled={isSubmitting || pageLoading}>
            {isSubmitting ? 'Guardando...' : (isEditMode ? 'Guardar Cambios' : 'Guardar Puerto')}
          </button>
        </form>
      )} 
    </div>
  );
}